import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SOCIAL_LABELS, type SocialConnectionSettings, type SocialProvider } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { SocialGuide } from './SocialGuide';

const errorText = (e: unknown, fallback: string): string => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const dayFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const RETURN_ERRORS: Record<string, string> = {
  ungueltig: 'Die Anmeldung war nicht gültig. Bitte versuchen Sie es noch einmal.',
  abgelaufen: 'Die Anmeldung hat zu lange gedauert. Bitte versuchen Sie es noch einmal.',
  abgebrochen: 'Sie haben den Zugriff nicht erteilt. Es wurde nichts verbunden.',
  dienst: 'Das Netzwerk hat die Anmeldung abgelehnt. Prüfen Sie App-ID und App-Geheimnis, ob die Rücksprungadresse in Ihrer App genau so eingetragen ist wie in Schritt 1 und ob Sie auf mindestens einer Seite Administrator sind.',
  'keine-seite': 'Sie haben Ihrer App bei der Anmeldung keine Facebook-Seite freigegeben. Verbinden Sie noch einmal und wählen Sie im Fenster von Facebook mindestens eine Seite aus.',
  'kein-instagram': 'Keine Ihrer Facebook-Seiten ist mit einem Instagram-Profikonto verknüpft. Stellen Sie Ihr Instagram-Konto auf ein Unternehmens- oder Creator-Konto um, verknüpfen Sie es mit Ihrer Facebook-Seite und verbinden Sie dann noch einmal.',
  'keine-app': 'Die Daten Ihrer App fehlen. Tragen Sie sie in Schritt 1 ein.',
};

const ACCOUNT_LABEL: Record<SocialProvider, string> = { facebook: 'Facebook-Seite', instagram: 'Instagram-Konto', linkedin: 'LinkedIn-Unternehmensseite', tiktok: 'Konto' };
/** So heißen App-ID und Geheimnis im Entwicklerbereich des jeweiligen Netzwerks */
const ID_LABEL: Record<SocialProvider, string> = { facebook: 'App-ID', instagram: 'App-ID', linkedin: 'Client ID', tiktok: 'Client Key' };
const SECRET_LABEL: Record<SocialProvider, string> = { facebook: 'App-Geheimschlüssel', instagram: 'App-Geheimschlüssel', linkedin: 'Primary Client Secret', tiktok: 'Client Secret' };

/** Ein Wert zum Übernehmen in die App beim Netzwerk, mit Knopf zum Kopieren */
function CopyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field">
      <span>{label}</span>
      <div className="copy-field">
        <input className="input" readOnly value={value} onFocus={(e) => e.target.select()} aria-label={label} />
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? 'Kopiert' : 'Kopieren'}
        </button>
      </div>
      {hint && <small className="muted">{hint}</small>}
    </div>
  );
}

/**
 * Facebook, Instagram, LinkedIn und TikTok verbinden (KON-190 bis KON-192, KON-269). Wirkia hat bei den Netzwerken keine eigene App. Der Mandant legt seine App selbst an, trägt
 * App-ID und Geheimnis in Schritt 1 ein und übernimmt die Adressen, die Wirkia ihm nennt, in seine App. In Schritt 2 meldet er sich mit seiner App beim Netzwerk an. Geheimnis und
 * Tokens liegen verschlüsselt auf dem Server und kommen nie in die Oberfläche.
 */
export function SocialIntegration({ provider }: { provider: SocialProvider }) {
  const client = useQueryClient();
  const [params] = useSearchParams();
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [manual, setManual] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const current = useQuery({ queryKey: ['integrations', 'social', provider], queryFn: async () => (await api<{ data: SocialConnectionSettings }>('GET', `/integrations/social/${provider}`)).data });
  const facebook = useQuery({
    queryKey: ['integrations', 'social', 'facebook'],
    queryFn: async () => (await api<{ data: SocialConnectionSettings }>('GET', '/integrations/social/facebook')).data,
    enabled: provider === 'instagram',
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['integrations'] });
  const returned = params.get('verbunden') ? { ok: true, text: `Verbunden. Wählen Sie unten, über welche ${ACCOUNT_LABEL[provider]} Wirkia arbeiten soll.` } : params.get('fehler') ? { ok: false, text: RETURN_ERRORS[params.get('fehler')!] ?? 'Die Verbindung hat nicht geklappt.' } : null;

  useEffect(() => {
    if (current.data) {
      setClientId(current.data.app.clientId ?? '');
      setFileName(current.data.app.verificationFileName ?? '');
    }
  }, [current.data]);

  const onSaved = (s: SocialConnectionSettings, text: string) => {
    client.setQueryData(['integrations', 'social', provider], s);
    refresh();
    setClientSecret('');
    setFileContent('');
    setMessage({ ok: true, text });
  };
  const saveApp = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api<{ data: SocialConnectionSettings }>('PUT', `/integrations/social/${provider}/app`, body)).data,
    onSuccess: (s) => onSaved(s, s.authorized ? 'Gespeichert.' : 'Die App ist gespeichert. Übernehmen Sie jetzt die Adressen unten in Ihre App und verbinden Sie dann in Schritt 2.'),
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const removeApp = useMutation({
    mutationFn: () => api('DELETE', `/integrations/social/${provider}/app`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['integrations'] });
      setClientId('');
      setMessage({ ok: true, text: 'App-Daten und Verbindung sind gelöscht.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });
  const connect = useMutation({
    mutationFn: async () => (await api<{ data: { url: string } }>('POST', `/integrations/social/${provider}/connect`)).data.url,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Verbindung konnte nicht gestartet werden.') }),
  });
  const choose = useMutation({
    mutationFn: async (accountId: string) => (await api<{ data: SocialConnectionSettings }>('PUT', `/integrations/social/${provider}/account`, { accountId })).data,
    onSuccess: (s) => {
      refresh();
      setMessage({ ok: true, text: `Wirkia arbeitet jetzt über „${s.label}“.` });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Die Auswahl hat nicht geklappt.') }),
  });
  const disconnect = useMutation({
    mutationFn: () => api('DELETE', `/integrations/${provider}`),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Die Verbindung ist getrennt. Die Daten Ihrer App bleiben gespeichert, Sie können jederzeit wieder verbinden.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Trennen hat nicht geklappt.') }),
  });

  if (current.isLoading) return <p className="muted">Wird geladen …</p>;
  const s = current.data;
  if (!s) return <div className="alert alert--error" role="alert">Die Verbindung konnte nicht geladen werden.</div>;
  const shown = message ?? returned;
  const expired = s.expiresAt ? Date.parse(s.expiresAt) < Date.now() : false;
  const hasApp = !!s.app.clientId && s.app.hasSecret;
  const label = SOCIAL_LABELS[provider];

  const submitApp = (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    if (s.authorized && clientId.trim() !== s.app.clientId && !window.confirm('Mit einer anderen App-ID gilt die bisherige Verbindung nicht mehr, Sie verbinden danach neu. Fortfahren?')) return;
    saveApp.mutate({ clientId: clientId.trim(), ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}) });
  };

  return (
    <>
      {s.status === 'error' && s.lastError && <div className="alert alert--error" role="alert">{s.lastError}</div>}
      {shown && <div className={`alert ${shown.ok ? 'alert--ok' : 'alert--error'}`} role={shown.ok ? 'status' : 'alert'}>{shown.text}</div>}

      <SocialGuide provider={provider} label={label} open={!s.authorized} />

      <form className="card" onSubmit={submitApp} noValidate>
        <h2>Schritt 1: Ihre {provider === 'instagram' || provider === 'facebook' ? 'Meta' : label}-App</h2>
        <p className="small muted">
          Wirkia veröffentlicht über Ihre eigene App bei {provider === 'instagram' || provider === 'facebook' ? 'Meta' : label}. Tragen Sie hier die Zugangsdaten Ihrer App ein. Das Geheimnis
          speichert Wirkia verschlüsselt und zeigt es danach nicht mehr an.
        </p>
        {provider === 'instagram' && facebook.data?.app.clientId && s.app.clientId !== facebook.data.app.clientId && (
          <div className="form-actions">
            <button className="btn btn--secondary" type="button" disabled={saveApp.isPending} onClick={() => { setMessage(null); saveApp.mutate({ sameAsFacebook: true }); }}>
              App von Facebook übernehmen ({facebook.data.app.clientId})
            </button>
          </div>
        )}
        <div className="form-grid">
          <label className="field">
            <span>{ID_LABEL[provider]}</span>
            <input className="input" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck={false} />
          </label>
          <label className="field">
            <span>{SECRET_LABEL[provider]}</span>
            <input className="input" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="new-password" placeholder={s.app.hasSecret ? 'gespeichert, nur zum Ändern eintragen' : ''} />
          </label>
        </div>
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={saveApp.isPending || !clientId.trim()}>{saveApp.isPending ? 'Wird gespeichert …' : 'App speichern'}</button>
          {hasApp && <button className="btn btn--ghost" type="button" disabled={removeApp.isPending} onClick={() => { if (window.confirm(`App-Daten und Verbindung zu ${label} löschen? Wirkia kann danach nicht mehr veröffentlichen.`)) removeApp.mutate(); }}>App-Daten löschen</button>}
        </div>

        <h3 className="social-app__copy">In Ihre App übernehmen</h3>
        <CopyField
          label={provider === 'linkedin' ? 'Authorized redirect URL' : provider === 'tiktok' ? 'Redirect URI' : 'Gültige OAuth-Redirect-URI'}
          value={s.app.redirectUri}
          hint="Muss Zeichen für Zeichen so in Ihrer App stehen, sonst lehnt das Netzwerk die Anmeldung ab."
        />
        {provider === 'facebook' && (s.app.webhookUrl ? (
          <>
            <CopyField label="Webhook: Rückruf-URL" value={s.app.webhookUrl} hint="In Ihrer App unter Webhooks beim Objekt „Page“ eintragen, Felder leadgen und messages abonnieren. Nur nötig für Lead-Formulare und Nachrichten." />
            {s.app.verifyToken && <CopyField label="Webhook: Prüftoken" value={s.app.verifyToken} />}
          </>
        ) : (
          <p className="small muted">Adresse und Prüftoken für den Webhook erscheinen, sobald Sie die App gespeichert haben.</p>
        ))}
        {provider === 'tiktok' && s.app.mediaPrefix && (
          <CopyField label="URL-Präfix für Ihre Bilder" value={s.app.mediaPrefix} hint="In Ihrer TikTok-App unter Content Posting API bei „URL properties“ als URL-Präfix hinzufügen. TikTok holt Ihre Bilder nur von bestätigten Adressen." />
        )}
      </form>

      {provider === 'tiktok' && hasApp && (
        <div className="card">
          <h2>TikTok: Bestätigung und Prüfung Ihrer App</h2>
          <p className="small">
            TikTok gibt Ihnen beim Hinzufügen des URL-Präfixes eine Prüfdatei zum Herunterladen, zum Beispiel <code>tiktok1a2b3c.txt</code>. Tragen Sie hier ihren Namen ein und fügen Sie ihren
            Inhalt ein. Wirkia legt die Datei unter Ihrem Präfix ab, danach klicken Sie in TikTok auf „Verify“.
          </p>
          <div className="form-grid">
            <label className="field"><span>Name der Prüfdatei</span><input className="input" value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="tiktok….txt" spellCheck={false} /></label>
            <label className="field"><span>Inhalt der Prüfdatei</span><input className="input" value={fileContent} onChange={(e) => setFileContent(e.target.value)} placeholder={s.app.verificationFileName ? 'hinterlegt, nur zum Ändern eintragen' : 'tiktok-developers-site-verification=…'} spellCheck={false} /></label>
          </div>
          <div className="form-actions">
            <button className="btn btn--secondary" type="button" disabled={saveApp.isPending || !fileName.trim() || !fileContent.trim()} onClick={() => { setMessage(null); saveApp.mutate({ verificationFileName: fileName.trim(), verificationContent: fileContent.trim() }); }}>Prüfdatei hinterlegen</button>
          </div>
          <label className="check">
            <input type="checkbox" checked={s.app.audited} disabled={saveApp.isPending} onChange={(e) => { setMessage(null); saveApp.mutate({ audited: e.target.checked }); }} />
            <span>TikTok hat meine App geprüft und freigegeben. Erst dann erscheinen Beiträge öffentlich, vorher nur für Sie selbst.</span>
          </label>
        </div>
      )}

      <div className="card">
        <h2>Schritt 2: {s.authorized ? 'Verbunden' : 'Verbinden'}</h2>
        {s.authorized ? (
          <p className="small">Wirkia arbeitet über <strong>{s.label}</strong>.{s.expiresAt && !expired ? ` Die Anmeldung gilt bis ${dayFormat.format(new Date(s.expiresAt))}, danach verbinden Sie neu.` : ''}</p>
        ) : hasApp ? (
          <p className="small">
            {provider === 'instagram'
              ? 'Sie melden sich über Facebook an und erlauben Ihrer App den Zugriff auf das Instagram-Konto, das mit Ihrer Facebook-Seite verknüpft ist. Wirkia sieht dabei nie Ihr Passwort.'
              : `Sie melden sich bei ${label} an und erlauben Ihrer App den Zugriff auf Ihre Seite. Wirkia sieht dabei nie Ihr Passwort.`}
          </p>
        ) : (
          <p className="small muted">Speichern Sie zuerst Ihre App in Schritt 1.</p>
        )}
        <div className="form-actions">
          <button className="btn btn--primary" type="button" disabled={connect.isPending || !hasApp} onClick={() => { setMessage(null); connect.mutate(); }}>
            {connect.isPending ? 'Einen Augenblick …' : s.authorized ? `Neu mit ${label} verbinden` : `Mit ${label} verbinden`}
          </button>
          {s.authorized && <button className="btn btn--secondary" type="button" disabled={disconnect.isPending} onClick={() => { if (window.confirm('Verbindung trennen? Wirkia kann danach nicht mehr in Ihrem Namen veröffentlichen. Die Daten Ihrer App bleiben gespeichert.')) disconnect.mutate(); }}>Trennen</button>}
        </div>
      </div>

      {s.authorized && provider !== 'tiktok' && (
        <div className="card">
          <h2>{ACCOUNT_LABEL[provider]}</h2>
          {s.accounts.length > 0 ? (
            <label className="field">
              <span>Wirkia arbeitet über</span>
              <select className="input" value={s.selectedId ?? ''} disabled={choose.isPending} onChange={(e) => { setMessage(null); choose.mutate(e.target.value); }}>
                {s.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
          ) : (
            <>
              <p className="small muted">LinkedIn hat keine Liste Ihrer Seiten geliefert. Tragen Sie die Kennung Ihrer Unternehmensseite ein: die Zahl in der Adresse Ihrer Seite (linkedin.com/company/<strong>12345678</strong>/admin).</p>
              <div className="form-row-end">
                <label className="field"><span>Kennung der Seite</span><input className="input" inputMode="numeric" value={manual} onChange={(e) => setManual(e.target.value)} placeholder={s.selectedId ?? '12345678'} /></label>
                <button className="btn btn--secondary" type="button" disabled={choose.isPending || !manual.trim()} onClick={() => { setMessage(null); choose.mutate(manual.trim()); }}>Übernehmen</button>
              </div>
            </>
          )}
        </div>
      )}

      {s.authorized && provider === 'facebook' && (
        <div className="card">
          <h2>Was Wirkia mit Facebook macht</h2>
          <ul className="small">
            <li>Beiträge veröffentlichen: in der CRM-Abteilung unter „Beiträge“, sofort oder geplant.</li>
            <li>Anfragen empfangen: Wer ein Lead-Formular Ihrer Seite ausfüllt, erscheint als Anfrage mit dem Typ „facebook-lead“. Dafür muss der Webhook aus Schritt 1 in Ihrer App eingetragen sein.</li>
            <li>Nachrichten beantworten: Nachrichten an Ihre Seite erscheinen unter „Live Chats“. Facebook erlaubt Antworten nur innerhalb von 24 Stunden nach der letzten Nachricht des Kunden.</li>
          </ul>
        </div>
      )}
    </>
  );
}
